import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ServiceModal = ({ isOpen, onClose, service, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    promotionalPrice: '',
    turnaroundTime: '',
    features: '',
    isActive: true,
    hasPromotion: false,
    promotionStartDate: '',
    promotionEndDate: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (service) {
      setFormData({
        name: service?.name || '',
        description: service?.description || '',
        basePrice: service?.basePrice?.toString() || '',
        promotionalPrice: service?.promotionalPrice?.toString() || '',
        turnaroundTime: service?.turnaroundTime || '',
        features: service?.features?.join(', ') || '',
        isActive: service?.isActive ?? true,
        hasPromotion: !!service?.promotionalPrice,
        promotionStartDate: service?.promotionStartDate || '',
        promotionEndDate: service?.promotionEndDate || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        basePrice: '',
        promotionalPrice: '',
        turnaroundTime: '',
        features: '',
        isActive: true,
        hasPromotion: false,
        promotionStartDate: '',
        promotionEndDate: '',
      });
    }
    setErrors({});
  }, [service, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = '서비스 이름을 입력해주세요';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = '서비스 설명을 입력해주세요';
    }

    if (!formData?.basePrice || parseFloat(formData?.basePrice) <= 0) {
      newErrors.basePrice = '유효한 기본 가격을 입력해주세요';
    }

    if (formData?.hasPromotion && formData?.promotionalPrice) {
      if (parseFloat(formData?.promotionalPrice) >= parseFloat(formData?.basePrice)) {
        newErrors.promotionalPrice = '프로모션 가격은 기본 가격보다 낮아야 합니다';
      }
    }

    if (!formData?.turnaroundTime?.trim()) {
      newErrors.turnaroundTime = '예상 소요시간을 입력해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    const serviceData = {
      id: service?.id || Date.now(),
      name: formData?.name?.trim(),
      description: formData?.description?.trim(),
      basePrice: parseFloat(formData?.basePrice),
      promotionalPrice: formData?.hasPromotion && formData?.promotionalPrice
        ? parseFloat(formData?.promotionalPrice)
        : null,
      turnaroundTime: formData?.turnaroundTime?.trim(),
      features: formData?.features?.split(',')?.map((f) => f?.trim())?.filter((f) => f),
      isActive: formData?.isActive,
      promotionStartDate: formData?.promotionStartDate || null,
      promotionEndDate: formData?.promotionEndDate || null,
      lastModified: new Date()?.toISOString(),
    };

    onSave(serviceData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card shadow-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">
            {service ? '서비스 수정' : '새 서비스 추가'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <Input
            label="서비스 이름"
            type="text"
            name="name"
            value={formData?.name}
            onChange={handleChange}
            placeholder="예: 베이직 케어"
            error={errors?.name}
            required
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              서비스 설명 <span className="text-destructive">*</span>
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              placeholder="서비스에 대한 상세 설명을 입력하세요"
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors?.description && (
              <p className="mt-1 text-xs text-destructive">{errors?.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="기본 가격 (원)"
              type="number"
              name="basePrice"
              value={formData?.basePrice}
              onChange={handleChange}
              placeholder="4000"
              error={errors?.basePrice}
              required
            />

            <Input
              label="예상 소요시간"
              type="text"
              name="turnaroundTime"
              value={formData?.turnaroundTime}
              onChange={handleChange}
              placeholder="2-3일"
              error={errors?.turnaroundTime}
              required
            />
          </div>

          <Checkbox
            label="프로모션 가격 설정"
            checked={formData?.hasPromotion}
            onChange={handleChange}
            name="hasPromotion"
          />

          {formData?.hasPromotion && (
            <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
              <Input
                label="프로모션 가격 (원)"
                type="number"
                name="promotionalPrice"
                value={formData?.promotionalPrice}
                onChange={handleChange}
                placeholder="3500"
                error={errors?.promotionalPrice}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="프로모션 시작일"
                  type="date"
                  name="promotionStartDate"
                  value={formData?.promotionStartDate}
                  onChange={handleChange}
                />

                <Input
                  label="프로모션 종료일"
                  type="date"
                  name="promotionEndDate"
                  value={formData?.promotionEndDate}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          <Input
            label="서비스 특징"
            type="text"
            name="features"
            value={formData?.features}
            onChange={handleChange}
            placeholder="특징을 쉼표로 구분하여 입력 (예: 기본 세척, 건조, 포장)"
            description="쉼표(,)로 구분하여 여러 특징을 입력할 수 있습니다"
          />

          <Checkbox
            label="서비스 활성화"
            checked={formData?.isActive}
            onChange={handleChange}
            name="isActive"
            description="활성화된 서비스만 고객에게 표시됩니다"
          />

          <div className="flex justify-end space-x-3 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" variant="default" iconName="Save" iconPosition="left">
              {service ? '수정 완료' : '추가하기'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceModal;